import React, { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Headset } from 'lucide-react';
import { openChat } from '../utils/cn';

// Floating "talk to an expert" bubble on the right edge of the viewport.
// Replaces the original vertical rotated tab — see git history if you need
// the old look. Visual vocabulary matches the hero address card + the $0
// down breathing pulse (cyan glow, glass background, 4s rhythm).
//
// Behavior:
//   Desktop hover → expand horizontally to the left, revealing copy + tel link
//   Desktop click on bubble area → open Chatbase widget (declared in index.html)
//   Mobile  click on bubble       → tel: dialer (no hover-expand on touch)
//   The phone-number text inside the expanded panel is always a tel: link,
//   so desktop users can click-to-call too. stopPropagation prevents the
//   container's chatbase handler from firing when the link is hit directly.
//
// Once-per-session attention pulse: when the user scrolls past ~600px
// (past the hero), the bubble does a single scale 1 → 1.1 → 1 to remind
// them help is available. Gated by sessionStorage.

const PHONE_TEL = 'tel:9379108791';
const PHONE_DISPLAY = '(937) 910-8791';
const PULSE_SESSION_KEY = 'openbooq_call_bubble_pulsed';
const MOBILE_BREAKPOINT_PX = 768;
const SCROLL_PULSE_THRESHOLD_PX = 600;

const StickyCallTab = () => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [attentionPulse, setAttentionPulse] = useState(false);

  // Viewport tracking — mobile gates expansion + swaps click destination.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const mq = window.matchMedia(`(max-width: ${MOBILE_BREAKPOINT_PX - 1}px)`);
    const sync = () => setIsMobile(mq.matches);
    sync();
    mq.addEventListener('change', sync);
    return () => mq.removeEventListener('change', sync);
  }, []);

  // Once-per-session scale pulse after the user scrolls past the hero.
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    if (sessionStorage.getItem(PULSE_SESSION_KEY) === 'true') return undefined;

    let pulsed = false;
    const handleScroll = () => {
      if (pulsed || window.scrollY <= SCROLL_PULSE_THRESHOLD_PX) return;
      pulsed = true;
      sessionStorage.setItem(PULSE_SESSION_KEY, 'true');
      setAttentionPulse(true);
      window.setTimeout(() => setAttentionPulse(false), 700);
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const openChatbaseOrCall = () => {
    if (isMobile) {
      window.location.href = PHONE_TEL;
      return;
    }
    // Delegated to the shared util — guarantees this widget and every
    // other "open chat" button on the site hit the same Chatbase
    // instance. If the global proxy is missing (ad blocker etc.) the
    // util tries a DOM click; if THAT fails, fall back to the phone.
    if (typeof window.chatbase === 'function' || document.querySelector('[id^="chatbase-"]')) {
      openChat();
      return;
    }
    window.location.href = PHONE_TEL;
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      openChatbaseOrCall();
    }
  };

  const handleTelClick = (e) => {
    // Stop the container's chatbase handler so the user's explicit
    // click-to-call always wins.
    e.stopPropagation();
  };

  const bubbleSize = isMobile ? 52 : 60;
  const iconSize = isMobile ? 22 : 24;
  const collapsedWidth = bubbleSize;
  const expandedWidth = 320;
  const showExpanded = isExpanded && !isMobile;

  return (
    // Outer wrapper owns the fixed positioning so the inner motion.div is
    // free to control transform (scale / future translates) without fighting
    // Tailwind's -translate-y-1/2. The `sticky-call-tab` class is a stable
    // hook for route-scoped CSS (Quote Check hides this bubble at phone
    // widths — see styles/tailwind.css, QA W1.1).
    <div className="sticky-call-tab fixed right-3 sm:right-4 top-1/2 -translate-y-1/2 z-50">
    <motion.div
      role="button"
      tabIndex={0}
      aria-label={`Talk to an OpenbooQ roofing expert — ${PHONE_DISPLAY}`}
      onClick={openChatbaseOrCall}
      onKeyDown={handleKeyDown}
      onMouseEnter={() => !isMobile && setIsExpanded(true)}
      onMouseLeave={() => !isMobile && setIsExpanded(false)}
      onFocus={() => !isMobile && setIsExpanded(true)}
      onBlur={() => !isMobile && setIsExpanded(false)}
      className="flex items-center justify-end cursor-pointer select-none overflow-hidden"
      style={{
        background: 'rgba(10, 22, 40, 0.72)',
        backdropFilter: 'blur(16px)',
        WebkitBackdropFilter: 'blur(16px)',
        border: '1px solid rgba(7, 176, 233, 0.45)',
        borderRadius: 9999,
        height: bubbleSize,
        padding: 4,
      }}
      animate={{
        width: showExpanded ? expandedWidth : collapsedWidth,
        scale: attentionPulse ? [1, 1.1, 1] : 1,
        filter: [
          'drop-shadow(0 4px 14px rgba(7, 176, 233, 0.35))',
          'drop-shadow(0 6px 28px rgba(7, 176, 233, 0.75))',
          'drop-shadow(0 4px 14px rgba(7, 176, 233, 0.35))',
        ],
      }}
      transition={{
        width: { type: 'spring', stiffness: 320, damping: 30, mass: 0.8 },
        scale: { duration: 0.7, ease: 'easeOut' },
        filter: { duration: 4, repeat: Infinity, ease: 'easeInOut', times: [0, 0.5, 1] },
      }}
    >
      <AnimatePresence>
        {showExpanded && (
          <motion.div
            key="panel"
            initial={{ opacity: 0, x: 8 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: 8 }}
            transition={{ duration: 0.22, ease: 'easeOut', delay: 0.08 }}
            className="flex-1 pl-4 pr-3 text-right leading-tight whitespace-nowrap"
          >
            <p className="text-[11px] font-bold text-white/90 tracking-wide">
              Talk to an OpenbooQ Roofing Expert
            </p>
            <a
              href={PHONE_TEL}
              onClick={handleTelClick}
              className="block text-base font-bold text-white mt-0.5 transition-colors"
              style={{ color: 'white' }}
              onMouseEnter={(e) => { e.currentTarget.style.color = '#07B0E9'; }}
              onMouseLeave={(e) => { e.currentTarget.style.color = 'white'; }}
            >
              {PHONE_DISPLAY}
            </a>
            <p className="text-[10px] font-medium text-white/60 mt-0.5">
              Available now · Free advice
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Icon disc — always visible, anchored to the right edge of the pill */}
      <div
        className="relative rounded-full flex items-center justify-center flex-shrink-0"
        style={{
          width: bubbleSize - 8,
          height: bubbleSize - 8,
          background: 'linear-gradient(135deg, rgba(7, 176, 233, 0.28), rgba(7, 176, 233, 0.10))',
        }}
      >
        <Headset size={iconSize} className="text-[#07B0E9]" strokeWidth={2} />
        {/* Online indicator — reuses .pulse-dot from tailwind.css. Absolute
            positioning here overrides .pulse-dot's default relative flow so
            it sits at the disc's top-right corner. */}
        <span
          className="pulse-dot"
          aria-hidden="true"
          style={{ position: 'absolute', top: 2, right: 2 }}
        />
      </div>
    </motion.div>
    </div>
  );
};

export default StickyCallTab;
